/**
 * DesignationsDialog — Iter 241
 * Small CRUD dialog for the workspace-scoped Designations master. Used
 * by the Estimate Effort Builder.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X } from "lucide-react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

export default function DesignationsDialog({ wsId, currency = "INR", onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [internal, setInternal] = useState("");
  const [level, setLevel] = useState("");
  const [bandMin, setBandMin] = useState("");
  const [bandMax, setBandMax] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/workspaces/${wsId}/estimate-designations`);
      setRows(data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (wsId) load(); }, [wsId]); // eslint-disable-line

  const add = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post(`/workspaces/${wsId}/estimate-designations`, {
        name: name.trim(),
        default_hourly_rate: Number(rate) || 0,
        internal_hourly_rate: Number(internal) || 0,
        level: level || undefined,
        band_min: bandMin === "" ? undefined : Number(bandMin),
        band_max: bandMax === "" ? undefined : Number(bandMax),
      });
      setName(""); setRate(""); setInternal(""); setLevel(""); setBandMin(""); setBandMax("");
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSaving(false); }
  };

  const patch = async (did, body) => {
    try {
      await api.patch(`/estimate-designations/${did}`, body);
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const remove = async (did) => {
    if (!window.confirm("Delete this designation? Existing efforts keep the label but stop linking to it.")) return;
    try {
      await api.delete(`/estimate-designations/${did}`);
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const csym = currency === "INR" ? "₹" : currency + " ";

  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-xl bg-background border border-border shadow-2xl"
           data-testid="designations-dialog">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="font-semibold text-sm">Designations · Effort master</div>
          <button type="button" onClick={onClose}
                   className="text-muted-foreground hover:text-foreground p-1"
                   data-testid="designations-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="border border-dashed border-border p-3 bg-muted/20">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Add new</div>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4">
                <label className="text-[10px] text-muted-foreground uppercase">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                        placeholder="Senior UX Designer"
                        className="w-full text-xs px-2 py-1 bg-background border border-border"
                        data-testid="designation-new-name" />
              </div>
              <div className="col-span-3">
                <label className="text-[10px] text-muted-foreground uppercase">Level</label>
                <input value={level} onChange={(e) => setLevel(e.target.value)}
                        placeholder="Senior · Lead"
                        list="designation-levels"
                        className="w-full text-xs px-2 py-1 bg-background border border-border"
                        data-testid="designation-new-level" />
                <datalist id="designation-levels">
                  <option value="Junior" />
                  <option value="Mid" />
                  <option value="Senior" />
                  <option value="Lead" />
                  <option value="Principal" />
                </datalist>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] text-muted-foreground uppercase">Billable /hr</label>
                <input type="number" value={rate}
                        onChange={(e) => setRate(e.target.value)}
                        placeholder="0"
                        className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums"
                        data-testid="designation-new-rate" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] text-muted-foreground uppercase">Cost /hr</label>
                <input type="number" value={internal}
                        onChange={(e) => setInternal(e.target.value)}
                        placeholder="0"
                        className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums"
                        data-testid="designation-new-internal" />
              </div>
              <div className="col-span-1">
                <button type="button" onClick={add} disabled={saving || !name.trim()}
                         className="w-full text-xs px-2 py-1.5 bg-primary text-primary-foreground disabled:opacity-50 font-medium flex items-center justify-center gap-1"
                         data-testid="designation-new-add">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              <div className="col-span-6 flex items-end gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-muted-foreground uppercase">Band min /hr</label>
                  <input type="number" value={bandMin}
                          onChange={(e) => setBandMin(e.target.value)}
                          placeholder="Optional"
                          className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums"
                          data-testid="designation-new-band-min" />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] text-muted-foreground uppercase">Band max /hr</label>
                  <input type="number" value={bandMax}
                          onChange={(e) => setBandMax(e.target.value)}
                          placeholder="Optional"
                          className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums"
                          data-testid="designation-new-band-max" />
                </div>
              </div>
              <div className="col-span-6 text-[10px] text-muted-foreground">
                Levels are free-form — pick from the dropdown or type your
                own ladder (Trainee → Analyst → Consultant → Partner).
              </div>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto border border-border">
            {loading ? (
              <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-center text-xs text-muted-foreground">
                No designations yet. Add your first one above.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-muted/40 border-b border-border">
                  <tr className="text-left text-[10px] uppercase text-muted-foreground">
                    <th className="px-2 py-1.5">Name</th>
                    <th className="px-2 py-1.5 w-24">Level</th>
                    <th className="px-2 py-1.5 w-28">Billable /hr</th>
                    <th className="px-2 py-1.5 w-28">Cost /hr</th>
                    <th className="px-2 py-1.5 w-40">Band</th>
                    <th className="px-2 py-1.5 w-16 text-right">Used</th>
                    <th className="px-2 py-1.5 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => (
                    <DesignationRow key={r.id} row={r} csym={csym}
                      onPatch={patch} onDelete={remove} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DesignationRow({ row, csym, onPatch, onDelete }) {
  const [name, setName] = useState(row.name || "");
  const [rate, setRate] = useState(row.default_hourly_rate ?? 0);
  const [internal, setInternal] = useState(row.internal_hourly_rate ?? 0);
  const [level, setLevel] = useState(row.level || "");
  const [bandMin, setBandMin] = useState(row.band_min ?? "");
  const [bandMax, setBandMax] = useState(row.band_max ?? "");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setName(row.name || "");
    setRate(row.default_hourly_rate ?? 0);
    setInternal(row.internal_hourly_rate ?? 0);
    setLevel(row.level || "");
    setBandMin(row.band_min ?? "");
    setBandMax(row.band_max ?? "");
    setDirty(false);
  }, [row.id, row.updated_at]);

  const save = async () => {
    await onPatch(row.id, {
      name: name.trim(),
      default_hourly_rate: Number(rate) || 0,
      internal_hourly_rate: Number(internal) || 0,
      level: level || null,
      band_min: bandMin === "" ? null : Number(bandMin),
      band_max: bandMax === "" ? null : Number(bandMax),
    });
    setDirty(false);
  };

  // Outside-band warning
  const outsideBand = (bandMin !== "" && Number(rate) < Number(bandMin))
                      || (bandMax !== "" && Number(rate) > Number(bandMax));
  return (
    <tr className={dirty ? "bg-amber-50/40" : ""} data-testid={`designation-row-${row.id}`}>
      <td className="px-2 py-1.5">
        <input value={name}
                onChange={(e) => { setName(e.target.value); setDirty(true); }}
                onBlur={() => dirty && save()}
                className="w-full text-xs px-2 py-1 bg-background border border-border"
                data-testid={`designation-name-${row.id}`} />
      </td>
      <td className="px-2 py-1.5">
        <input value={level}
                onChange={(e) => { setLevel(e.target.value); setDirty(true); }}
                onBlur={() => dirty && save()}
                placeholder="—"
                className="w-full text-xs px-2 py-1 bg-background border border-border"
                data-testid={`designation-level-${row.id}`} />
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-muted-foreground">{csym}</span>
          <input type="number" value={rate}
                  onChange={(e) => { setRate(e.target.value); setDirty(true); }}
                  onBlur={() => dirty && save()}
                  className={`w-full text-xs px-2 py-1 bg-background border tabular-nums ${outsideBand ? "border-rose-300 bg-rose-50" : "border-border"}`}
                  title={outsideBand ? "Rate is outside the configured band" : undefined}
                  data-testid={`designation-rate-${row.id}`} />
        </div>
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-muted-foreground">{csym}</span>
          <input type="number" value={internal}
                  onChange={(e) => { setInternal(e.target.value); setDirty(true); }}
                  onBlur={() => dirty && save()}
                  placeholder="0"
                  className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums"
                  data-testid={`designation-internal-${row.id}`} />
        </div>
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1">
          <input type="number" value={bandMin}
                  onChange={(e) => { setBandMin(e.target.value); setDirty(true); }}
                  onBlur={() => dirty && save()}
                  placeholder="min"
                  className="w-16 text-xs px-1 py-1 bg-background border border-border tabular-nums"
                  data-testid={`designation-band-min-${row.id}`} />
          <span className="text-[10px] text-muted-foreground">–</span>
          <input type="number" value={bandMax}
                  onChange={(e) => { setBandMax(e.target.value); setDirty(true); }}
                  onBlur={() => dirty && save()}
                  placeholder="max"
                  className="w-16 text-xs px-1 py-1 bg-background border border-border tabular-nums"
                  data-testid={`designation-band-max-${row.id}`} />
        </div>
      </td>
      <td className="px-2 py-1.5 text-right text-muted-foreground tabular-nums">
        {row.usage_count ?? 0}
      </td>
      <td className="px-2 py-1.5 text-right">
        <button type="button" onClick={() => onDelete(row.id)}
                 className="text-muted-foreground hover:text-rose-500 p-1"
                 aria-label="Delete"
                 data-testid={`designation-remove-${row.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </td>
    </tr>
  );
}
